import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Autocomplete,
  Stack,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '../../contexts/AuthContext';
import tarefaService, { TarefaFiltros } from '../../services/tarefaService';
import usuarioService from '../../services/usuarioService';
import departamentoService from '../../services/departamentoService';
import { Tarefa, Usuario, Departamento } from '../../types';
import { toast } from 'react-toastify';
import { obterCorPrioridade } from '../../utils/coresPrioridade';
import TaskDetailsModal from '../kanban/TaskDetailsModal';

const STATUS_OPCOES = [
  { valor: 'Pendente', label: 'Pendente' },
  { valor: 'A iniciar', label: 'A iniciar' },
  { valor: 'Em Andamento', label: 'Em Andamento' },
  { valor: 'Concluída', label: 'Concluída' }
];

const PRIORIDADE_OPCOES = [
  { valor: 'baixa', label: 'Baixa' },
  { valor: 'media', label: 'Média' },
  { valor: 'alta', label: 'Alta' },
  { valor: 'urgente', label: 'Urgente' }
];

const BuscaAvancadaTarefas: React.FC = () => {
  const { usuario } = useAuth();

  // Estados dos filtros
  const [filtros, setFiltros] = useState<TarefaFiltros>({
    pagina: 1,
    limite: 50
  });
  const [texto, setTexto] = useState('');
  const [numero, setNumero] = useState('');
  const [exibir, setExibir] = useState<'ativas' | 'arquivadas' | 'todas'>('ativas');
  const [responsaveisSelecionados, setResponsaveisSelecionados] = useState<Usuario[]>([]);
  const [criadoresSelecionados, setCriadoresSelecionados] = useState<Usuario[]>([]);
  const [buscaExecutada, setBuscaExecutada] = useState(false);

  // Estados de dados
  const [tarefas, setTarefas] = useState<Tarefa[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [tarefaSelecionada, setTarefaSelecionada] = useState<Tarefa | null>(null);
  const [modalAberto, setModalAberto] = useState(false);

  // Carregar usuários e departamentos (para líderes e admins)
  useEffect(() => {
    const carregarDados = async () => {
      try {
        if (usuario?.eh_lider || usuario?.tipo === 'admin') {
          const [usuariosResponse, departamentosResponse] = await Promise.all([
            usuarioService.obterTodos(),
            departamentoService.obterTodos()
          ]);
          if (departamentosResponse.success && departamentosResponse.departamentos) {
            setDepartamentos(departamentosResponse.departamentos);
          }
          if (usuariosResponse.success && usuariosResponse.usuarios) {
            if (usuario?.eh_lider && usuario?.tipo !== 'admin') {
              // Filtrar usuários de TODOS os departamentos que o líder gerencia
              const deptoIds = new Set(
                (departamentosResponse.departamentos || []).map((d: any) => d.id)
              );
              const usuariosFiltrados = usuariosResponse.usuarios.filter(
                (u: any) => deptoIds.has(u.departamento_id)
              );
              setUsuarios(usuariosFiltrados);
            } else {
              setUsuarios(usuariosResponse.usuarios);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    };
    carregarDados();
  }, [usuario]);

  // Função de busca
  const buscar = async () => {
    setBuscaExecutada(true);
    try {
      setCarregando(true);
      const filtrosCompletos: any = {
        ...filtros,
        texto: texto || undefined,
        numero: numero.replace(/^#/, '').trim() || undefined,
        arquivadas: exibir === 'arquivadas' ? true : exibir === 'todas' ? undefined : false,
        incluirConcluidas: true,
        // Enviar múltiplos IDs como array
        responsaveis: responsaveisSelecionados.length > 0 
          ? responsaveisSelecionados.map(u => u.id) 
          : undefined,
        criadores: criadoresSelecionados.length > 0 
          ? criadoresSelecionados.map(u => u.id) 
          : undefined
      };

      const resultado = await tarefaService.buscarTarefas(filtrosCompletos);

      // Mapear dados do backend (snake_case) para o formato esperado (camelCase)
      const tarefasMapeadas = (resultado.tarefas || []).map((t: any) => ({
        ...t,
        responsavel: t.responsavel_id ? {
          id: t.responsavel_id,
          nome: t.responsavel_nome || '',
          email: t.responsavel_email || ''
        } : undefined,
        criador: t.criador_id ? {
          id: t.criador_id,
          nome: t.criador_nome || '',
          email: t.criador_email || ''
        } : undefined,
        departamento: t.departamento_id ? {
          id: t.departamento_id,
          nome: t.departamento_nome || ''
        } : undefined,
        dataCriacao: t.data_criacao,
        dataVencimento: t.data_vencimento,
        dataAtualizacao: t.data_atualizacao,
        dataConclusao: t.data_conclusao
      }));
      
      setTarefas(tarefasMapeadas);
      setTotal(resultado.total);
      setTotalPaginas(resultado.totalPaginas);
    } catch (error) {
      console.error('Erro na busca:', error);
      toast.error('Erro ao buscar tarefas');
    } finally {
      setCarregando(false);
    }
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltros({ pagina: 1, limite: 50 });
    setTexto('');
    setNumero('');
    setExibir('ativas');
    setResponsaveisSelecionados([]);
    setCriadoresSelecionados([]);
    setTarefas([]);
    setTotal(0);
    setBuscaExecutada(false);
  };

  const visualizarTarefa = (tarefa: Tarefa) => {
    setTarefaSelecionada(tarefa);
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setTarefaSelecionada(null);
  };

  // Mudar página
  const handleChangePage = (event: unknown, newPage: number) => {
    setFiltros({ ...filtros, pagina: newPage + 1 });
  };

  // Mudar limite por página
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFiltros({ ...filtros, limite: parseInt(event.target.value, 10), pagina: 1 });
  };

  // Buscar ao mudar página
  useEffect(() => {
    if ((filtros.pagina && filtros.pagina > 1) || tarefas.length > 0) {
      buscar();
    }
  }, [filtros.pagina, filtros.limite]);

  // Função para obter cor do status
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'Pendente': '#3498db',
      'Solicitado': '#3498db',
      'A iniciar': '#1abc9c',
      'Em Andamento': '#e67e22',
      'Em Execução': '#e67e22',
      'Aguardando Resposta': '#f1c40f',
      'Aguardando Aprovação': '#f1c40f',
      'Ajustes Requisitados': '#9b59b6',
      'Concluída': '#2ecc71',
      'Finalizada': '#2ecc71'
    };
    return colors[status] || '#95a5a6';
  };

  // Mesma paleta de prioridade do resto do sistema (antes usava nomes de
  // cor aproximados do MUI — 'primary'/'warning'/'error' — que não batiam
  // com a cor real usada no Kanban/modal de tarefa para a mesma prioridade).

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <SearchIcon sx={{ mr: 1 }} />
          Busca Avançada de Tarefas
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Busque tarefas usando múltiplos filtros
        </Typography>
      </Box>

      {/* Formulário de Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>

          <Stack spacing={2}>
            {/* Linha 1: Busca, Número e Status */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Buscar por título ou descrição"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder="Digite palavras-chave..."
                variant="outlined"
                onKeyPress={(e) => e.key === 'Enter' && buscar()}
              />
              <TextField
                fullWidth
                label="Número da tarefa"
                value={numero}
                onChange={(e) => setNumero(e.target.value)}
                placeholder="#8716"
                variant="outlined"
                onKeyPress={(e) => e.key === 'Enter' && buscar()}
              />
              <FormControl fullWidth variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filtros.status || ''}
                  onChange={(e) => setFiltros({ ...filtros, status: e.target.value || undefined })}
                  label="Status"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Pendente">Pendente</MenuItem>
                  <MenuItem value="A iniciar">A iniciar</MenuItem>
                  <MenuItem value="Em Andamento">Em Andamento</MenuItem>
                  <MenuItem value="Concluída">Concluída</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Linha 2: Prioridade e Datas */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Prioridade</InputLabel>
                <Select
                  value={filtros.prioridade || ''}
                  onChange={(e) => setFiltros({ ...filtros, prioridade: e.target.value || undefined })}
                  label="Prioridade"
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="baixa">Baixa</MenuItem>
                  <MenuItem value="media">Média</MenuItem>
                  <MenuItem value="alta">Alta</MenuItem>
                  <MenuItem value="urgente">Urgente</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                type="date"
                label="Data de Criação (Início)"
                value={filtros.dataInicio || ''}
                onChange={(e) => setFiltros({ ...filtros, dataInicio: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
              <TextField
                fullWidth
                type="date"
                label="Data de Criação (Fim)"
                value={filtros.dataFim || ''}
                onChange={(e) => setFiltros({ ...filtros, dataFim: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </Box>

            {/* Linha 3: Filtros para líderes e admins */}
            {(usuario?.eh_lider || usuario?.tipo === 'admin') && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                <Autocomplete
                  options={departamentos}
                  getOptionLabel={(option) => option.nome}
                  value={departamentos.find(d => d.id === filtros.departamento) || null}
                  onChange={(event, newValue) => setFiltros({ ...filtros, departamento: newValue?.id || undefined })}
                  renderInput={(params) => (
                    <TextField {...params} label="Departamento" variant="outlined" />
                  )}
                />
                <Autocomplete
                  multiple
                  options={usuarios}
                  getOptionLabel={(option) => {
                    const dept = option.departamento_nome || 'Sem departamento';
                    return `${option.nome} (${dept})`;
                  }}
                  value={responsaveisSelecionados}
                  onChange={(event, newValue) => setResponsaveisSelecionados(newValue)}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter(option =>
                      option.nome.toLowerCase().includes(inputValue.toLowerCase()) ||
                      (option.departamento_nome || '').toLowerCase().includes(inputValue.toLowerCase())
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Responsável" variant="outlined" placeholder="Selecione um ou mais..." />
                  )}
                />
                <Autocomplete
                  multiple
                  options={usuarios}
                  getOptionLabel={(option) => {
                    const dept = option.departamento_nome || 'Sem departamento';
                    return `${option.nome} (${dept})`;
                  }}
                  value={criadoresSelecionados}
                  onChange={(event, newValue) => setCriadoresSelecionados(newValue)}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter(option =>
                      option.nome.toLowerCase().includes(inputValue.toLowerCase()) ||
                      (option.departamento_nome || '').toLowerCase().includes(inputValue.toLowerCase())
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Criador" variant="outlined" placeholder="Selecione um ou mais..." />
                  )}
                />
              </Box>
            )}

            {/* Linha 4: Exibir */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Exibir</InputLabel>
                <Select
                  value={exibir}
                  onChange={(e) => setExibir(e.target.value as 'ativas' | 'arquivadas' | 'todas')}
                  label="Exibir"
                >
                  <MenuItem value="ativas">Tarefas Ativas</MenuItem>
                  <MenuItem value="arquivadas">Tarefas Arquivadas</MenuItem>
                  <MenuItem value="todas">Todas as Tarefas</MenuItem>
                </Select>
              </FormControl>
              <Box />
              <Box />
            </Box>

            {/* Botões */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={limparFiltros}
              >
                Limpar
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={buscar}
                disabled={carregando}
              >
                {carregando ? 'Buscando...' : 'Buscar'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Mensagem informativa */}
      {tarefas.length === 0 && !carregando && (
        <Alert severity={buscaExecutada ? 'warning' : 'info'} sx={{ mb: 3 }}>
          {buscaExecutada
            ? 'Nenhuma tarefa encontrada com os filtros informados. Tente ajustar os critérios de busca.'
            : 'Use os filtros acima e clique em "Buscar" para encontrar tarefas'}
        </Alert>
      )}

      {/* Resultados */}
      {carregando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : tarefas.length > 0 ? (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Prioridade</TableCell>
                  <TableCell>Responsável</TableCell>
                  <TableCell>Departamento</TableCell>
                  <TableCell>Data Criação</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tarefas.map((tarefa) => (
                  <TableRow key={tarefa.id} hover>
                    <TableCell>{tarefa.id}</TableCell>
                    <TableCell>{tarefa.titulo}</TableCell>
                    <TableCell>
                      <Chip
                        label={tarefa.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(tarefa.status),
                          color: 'white'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tarefa.prioridade}
                        size="small"
                        sx={{
                          backgroundColor: obterCorPrioridade(tarefa.prioridade),
                          color: '#fff',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>{tarefa.responsavel?.nome || '-'}</TableCell>
                    <TableCell>{tarefa.departamento?.nome || '-'}</TableCell>
                    <TableCell>
                      {new Date(tarefa.dataCriacao).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => visualizarTarefa(tarefa)}
                        title="Ver detalhes"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={(filtros.pagina || 1) - 1}
            onPageChange={handleChangePage}
            rowsPerPage={filtros.limite || 50}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Tarefas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      ) : (
        <Alert severity="info">
          Nenhuma tarefa encontrada. Use os filtros acima para buscar.
        </Alert>
      )}

      {tarefaSelecionada && (
        <TaskDetailsModal
          tarefa={tarefaSelecionada}
          aberto={modalAberto}
          onFechar={fecharModal}
        />
      )}
    </Box>
  );
};

export default BuscaAvancadaTarefas;
